import dayjs from "dayjs";
import { PaletteMode } from "@mui/material";
import {
  getPlatformDisplayName,
  resolveBriefingPlatformIcon,
} from "../../utils/iconUtils";
import type {
  ClientPortalCampaign,
  ClientPortalStatus,
  ClientPortalSubcampaign,
} from "../../store/apis/clientPortalApi";

// Utilidades de la vista de cliente: formato de cifras, fechas, plataformas y
// colores de las gráficas. Todo lo que ve el cliente pasa por aquí para que la
// nomenclatura sea la misma en todas las pantallas.

/**
 * Ancho fijo del sidebar de cliente; las páginas dejan ese margen. Es el mismo
 * que el del sidebar general desplegado, para las páginas comunes (Ajustes).
 */
export const CLIENT_SIDEBAR_WIDTH = 250;
/** El sidebar general plegado (el resto de roles también ven el inicio). */
export const COLLAPSED_SIDEBAR_WIDTH = 80;

/**
 * Tinte azul eléctrico: claro en claro y profundo en oscuro, para que el texto
 * (oscuro o claro según el modo) se siga leyendo bien. Es el hover de las filas
 * de la tabla de campañas (antes también el fondo del sidebar).
 */
export const SIDEBAR_BACKGROUND = {
  light: "#D6E4FF",
  dark: "#061c64",
} as const;

// Las cifras llevan el punto de miles desde los cuatro dígitos (1.000, 7.423) en
// todos los idiomas: en inglés el navegador pondría coma y en español no agrupa
// las de cuatro. El decimal sigue el idioma, salvo si la cifra ya lleva punto de
// miles: entonces va con coma (1.234,50€, no 1.234.50€).

/** Pone el punto de miles en la parte entera de una cifra formateada sin agrupar. */
const withThousands = (
  parts: Intl.NumberFormatPart[]
): Intl.NumberFormatPart[] => {
  const grouped = parts.some(
    (part) => part.type === "integer" && part.value.length > 3
  );
  return parts.map((part) => {
    if (part.type === "integer")
      return {
        ...part,
        value: part.value.replace(/\B(?=(\d{3})+(?!\d))/g, "."),
      };
    if (part.type === "decimal" && grouped) return { ...part, value: "," };
    return part;
  });
};

const formatWithThousands = (
  format: Intl.NumberFormat,
  value: number
): string =>
  withThousands(format.formatToParts(value))
    .map((part) => part.value)
    .join("");

/** 1,2 M · 845 K · 312 */
export const formatCompact = (value: number, language: string): string =>
  formatWithThousands(
    new Intl.NumberFormat(language, {
      notation: "compact",
      maximumFractionDigits: value >= 1000 ? 1 : 0,
      useGrouping: false,
    }),
    value
  );

/** 7.423 */
export const formatNumber = (value: number, language: string): string =>
  formatWithThousands(
    new Intl.NumberFormat(language, {
      maximumFractionDigits: 0,
      useGrouping: false,
    }),
    value
  );

export const formatPercent = (
  value: number,
  language: string,
  digits = 0
): string =>
  formatWithThousands(
    new Intl.NumberFormat(language, {
      style: "percent",
      maximumFractionDigits: digits,
      minimumFractionDigits: digits,
      useGrouping: false,
    }),
    value
  );

/**
 * La moneda siempre detrás de la cifra y pegada (371€, 1.083€, 1,2M€), en todos
 * los idiomas: en inglés el navegador la pone delante (€371).
 */
const currencyLast = (format: Intl.NumberFormat, value: number): string => {
  const parts = withThousands(format.formatToParts(value));
  const symbol = parts
    .filter((part) => part.type === "currency")
    .map((part) => part.value)
    .join("");
  const number = parts
    .filter((part) => part.type !== "currency")
    .map((part) => part.value)
    .join("")
    .replace(/[\s\u00a0\u202f]+/g, "");
  return `${number}${symbol}`;
};

/** 45.230€ · 1,2M€ (sin decimales: es una cifra de resumen) */
export const formatMoney = (
  value: number,
  currency: string,
  language: string
): string =>
  currencyLast(
    new Intl.NumberFormat(language, {
      style: "currency",
      currency,
      notation: value >= 1_000_000 ? "compact" : "standard",
      minimumFractionDigits: 0,
      maximumFractionDigits: value >= 1_000_000 ? 1 : 0,
      useGrouping: false,
    }),
    value
  );

/** 2,35€ (coste por unidad: con céntimos) */
export const formatUnitMoney = (
  value: number,
  currency: string,
  language: string
): string =>
  currencyLast(
    new Intl.NumberFormat(language, {
      style: "currency",
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      useGrouping: false,
    }),
    value
  );

/** Coste por unidad (por mil con `perThousand`), o null si no hay con qué. */
export const costPer = (
  investment: number,
  units: number | null | undefined,
  perThousand = false
): number | null =>
  units && units > 0 && investment > 0
    ? (investment * (perThousand ? 1000 : 1)) / units
    : null;

/** Modelos de compra cuyo coste por unidad es por mil. */
export const isPerThousand = (model: string | null | undefined): boolean =>
  model === "CPM" || model === "CPMV";

/** "3 sep" */
export const formatShortDate = (date: string, language: string): string =>
  new Intl.DateTimeFormat(language, { day: "numeric", month: "short" })
    .format(dayjs(date).toDate())
    .replace(".", "");

/** "3 sep 2026" */
export const formatDate = (date: string | null, language: string): string =>
  date
    ? new Intl.DateTimeFormat(language, {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
        .format(dayjs(date).toDate())
        .replace(".", "")
    : "—";

// Plataformas de producto (las del briefing) con su nombre comercial. Las
// plataformas técnicas que hay detrás (DV360, Taboola...) se muestran con el
// mismo nombre que ya ve el cliente en el resto de la app.
const PLATFORM_WORDS: Record<string, string> = {
  BEAPOLO: "BeApolo",
  YOUTUBE: "YouTube",
  TIKTOK: "TikTok",
  CHATGPT: "ChatGPT",
  LINKEDIN: "LinkedIn",
  TRES: "3",
  CAT: "Cat",
};
const UPPERCASE_WORDS = new Set([
  "CTV",
  "OTT",
  "DOOH",
  "OOH",
  "LG",
  "DCO",
  "TV",
]);
const TECHNICAL_PLATFORMS = new Set([
  "DV360",
  "BEAPOLO",
  "SPOTIFY_ADS",
  "HIVESTACK",
  "AMAZON_DSP",
  "CTV",
  "META",
  "TIKTOK",
  "REDDIT",
]);
// El cliente ve "NatAds" en las de nativo, sin el proveedor (Outbrain o Taboola).
const PLATFORM_NAMES: Record<string, string> = {
  NATADS: "NatAds",
  OUTBRAIN: "NatAds",
  TABOOLA: "NatAds",
};

/** Líneas sin plataforma decidida: no se pinta ni icono ni nombre. */
export const isUnknownPlatform = (platform: string): boolean =>
  !platform || platform.toUpperCase() === "UNDETERMINED";

export const platformLabel = (platform: string): string => {
  const key = platform.toUpperCase();
  if (isUnknownPlatform(platform)) return "—";
  if (PLATFORM_NAMES[key]) return PLATFORM_NAMES[key];
  if (
    key.startsWith("DV360") ||
    key.startsWith("GOOGLE_ADS") ||
    TECHNICAL_PLATFORMS.has(key)
  )
    return getPlatformDisplayName(key.startsWith("DV360") ? "dv360" : key);
  return key
    .split("_")
    .map((word) =>
      PLATFORM_WORDS[word]
        ? PLATFORM_WORDS[word]
        : UPPERCASE_WORDS.has(word)
          ? word
          : word.charAt(0) + word.slice(1).toLowerCase()
    )
    .join(" ");
};

export const platformIcon = (platform: string): string =>
  resolveBriefingPlatformIcon(platform).iconPath;

/** Google Ads (de campaña o de línea: GOOGLE_ADS, GOOGLE_ADS_SEM...). */
export const isGoogleAdsPlatform = (platform: string): boolean =>
  platform.toUpperCase().startsWith("GOOGLE_ADS");

/**
 * Si el objetivo enseña dónde acabará al ritmo de ayer. En Google Ads, NatAds
 * (Outbrain y Taboola) y BeApolo (y lo que va por DV360) no.
 */
export const showsGoalProjection = (platform: string): boolean => {
  const key = platform.toUpperCase();
  return !(
    isGoogleAdsPlatform(key) ||
    key === "NATADS" ||
    key === "OUTBRAIN" ||
    key === "TABOOLA" ||
    key.startsWith("BEAPOLO") ||
    key.startsWith("DV360")
  );
};

/** Parte del objetivo conseguida (0.5 = 50 %), o null sin plan. */
export const goalProgress = (
  sub: Pick<ClientPortalSubcampaign, "delivered" | "planned">
): number | null =>
  sub.planned && sub.planned > 0 ? sub.delivered / sub.planned : null;

/** +11 % · −4 % · 0 % */
export const formatSignedPercent = (
  value: number,
  language: string,
  digits = 0
): string =>
  formatWithThousands(
    new Intl.NumberFormat(language, {
      style: "percent",
      signDisplay: "exceptZero",
      maximumFractionDigits: digits,
      minimumFractionDigits: digits,
      useGrouping: false,
    }),
    value
  );

/** Lo que mide el avance de un objetivo: lo conseguido y lo previsto, acumulados. */
export interface GoalSource {
  delivered: number;
  planned: number | null;
  series: Pick<SeriesPoint, "date" | "delivered" | "planned">[];
}

/**
 * Cómo va un objetivo, con las mismas cuentas que el WIP:
 * - `expected`: lo que tocaba llevar hasta ayer (reparto lineal del objetivo);
 * - `deviation`: lo conseguido frente a eso (11 % llevado de 12 % debido = −8 %);
 * - `projected`: dónde acabará si cada día que queda suma lo mismo que ayer
 *   (`projectedVolume`), frente al objetivo. Sin nada ayer no se proyecta
 *   (`zeroData`): no se sabe si se ha parado.
 * Los porcentajes, sobre el objetivo total. null antes de empezar o sin plan.
 */
export interface GoalPacing {
  reached: number;
  expected: number | null;
  deviation: number | null;
  projected: number | null;
  projectedVolume: number | null;
  zeroData: boolean;
}

export const goalPacing = (goal: GoalSource): GoalPacing | null => {
  const total = goal.planned;
  if (!total || total <= 0) return null;
  const { series } = goal;
  let last = -1;
  series.forEach((point, index) => {
    if (point.delivered !== null) last = index;
  });
  const reached = goal.delivered / total;
  const expectedVolume = last >= 0 ? series[last].planned : null;
  if (!expectedVolume || expectedVolume <= 0)
    return {
      reached,
      expected: null,
      deviation: null,
      projected: null,
      projectedVolume: null,
      zeroData: false,
    };
  const delivered = series[last].delivered as number;
  const daysLeft = series.length - 1 - last;
  const yesterday =
    delivered - (last > 0 ? (series[last - 1].delivered ?? 0) : 0);
  const projectedVolume =
    daysLeft === 0
      ? delivered
      : yesterday > 0
        ? delivered + yesterday * daysLeft
        : null;
  return {
    reached,
    expected: expectedVolume / total,
    deviation: delivered / expectedVolume - 1,
    projected: projectedVolume === null ? null : projectedVolume / total,
    projectedVolume,
    zeroData: projectedVolume === null,
  };
};

/** Margen para estar "en ritmo", el mismo que usa el WIP (±10 %). */
const PACE_TOLERANCE = 0.1;

export type PaceOutcome = "behind" | "onTrack" | "ahead";

export const paceOutcome = (variation: number): PaceOutcome =>
  variation > PACE_TOLERANCE
    ? "ahead"
    : variation < -PACE_TOLERANCE
      ? "behind"
      : "onTrack";

/**
 * El objetivo de una campaña: la suma de sus subcampañas, día a día. Antes de
 * que empiece una subcampaña cuenta 0 y, cuando ya ha acabado, lo que llevaba.
 * Los días que aún no han pasado (en ninguna) quedan sin conseguido.
 */
export const combineGoals = (goals: GoalSource[]): GoalSource => {
  const dates = [
    ...new Set(goals.flatMap((g) => g.series.map((p) => p.date))),
  ].sort();
  const lastReal = goals
    .flatMap((g) => g.series.filter((p) => p.delivered !== null))
    .reduce((max, p) => (p.date > max ? p.date : max), "");
  const series = dates.map((date) => {
    let delivered = 0;
    let planned = 0;
    for (const goal of goals) {
      const upTo = goal.series.filter((p) => p.date <= date);
      const real = upTo.filter((p) => p.delivered !== null);
      delivered += real.length
        ? (real[real.length - 1].delivered as number)
        : 0;
      planned += upTo.length ? (upTo[upTo.length - 1].planned ?? 0) : 0;
    }
    return { date, delivered: date <= lastReal ? delivered : null, planned };
  });
  return {
    delivered: goals.reduce((sum, g) => sum + g.delivered, 0),
    planned: goals.reduce((sum, g) => sum + (g.planned ?? 0), 0) || null,
    series,
  };
};

/** Cómo se ven las gráficas de avance: lo de cada día o la suma hasta ese día. */
export type ChartMode = "daily" | "cumulative";

type SeriesPoint = ClientPortalSubcampaign["series"][number];

/**
 * La serie llega acumulada; en diario cada punto es lo que suma ese día
 * respecto al anterior. Los días sin dato siguen sin dato.
 */
export const toDailySeries = (series: SeriesPoint[]): SeriesPoint[] =>
  series.map((point, index) => {
    const previous = index > 0 ? series[index - 1] : null;
    const step = (value: number | null, before: number | null | undefined) =>
      value === null ? null : value - (before ?? 0);
    return {
      ...point,
      delivered: step(point.delivered, previous?.delivered),
      planned: step(point.planned, previous?.planned),
      investment: step(point.investment, previous?.investment),
      plannedInvestment: step(
        point.plannedInvestment,
        previous?.plannedInvestment
      ),
    };
  });

/**
 * Predicción de una serie acumulada, la misma regla que la gráfica del inicio:
 * desde el último día con dato, cada día que queda suma lo mismo que ese día.
 * Da cada día en acumulado o en diario (null antes del último día con dato), o
 * null si no quedan días por delante o no hay dato.
 */
export const forecastSeries = (
  cumulative: (number | null)[],
  chartMode: ChartMode
): (number | null)[] | null => {
  let last = -1;
  cumulative.forEach((value, index) => {
    if (value !== null) last = index;
  });
  if (last < 0 || last === cumulative.length - 1) return null;
  const reached = cumulative[last] as number;
  const step = reached - (last > 0 ? (cumulative[last - 1] ?? 0) : 0);
  return cumulative.map((_, index) =>
    index < last
      ? null
      : chartMode === "daily"
        ? step
        : reached + step * (index - last)
  );
};

/**
 * Suma de una métrica de todas las subcampañas en los últimos días hasta
 * `asOfDate` incluido. Los días sin datos cuentan como 0.
 */
export const lastDaysTotals = (
  subs: ClientPortalSubcampaign[],
  metric: "impressions" | "clicks" | "investment",
  asOfDate: string,
  days = 7
): { date: string; value: number }[] => {
  const end = dayjs(asOfDate);
  const totals = new Map<string, number>();
  for (let i = days - 1; i >= 0; i--)
    totals.set(end.subtract(i, "day").format("YYYY-MM-DD"), 0);
  for (const sub of subs)
    // La inversión llega acumulada: se pasa a lo de cada día.
    for (const point of metric === "investment"
      ? toDailySeries(sub.series)
      : sub.series) {
      const current = totals.get(point.date);
      if (current !== undefined)
        totals.set(point.date, current + (point[metric] ?? 0));
    }
  return [...totals].map(([date, value]) => ({ date, value }));
};

export const statusColor = (status: ClientPortalStatus): string =>
  status === "LIVE" ? "#0ca30c" : status === "UPCOMING" ? "#fab219" : "#9e9e9e";

// Paleta categórica validada (orden fijo, nunca se recicla): la serie N lleva
// siempre el color N. Cada modo tiene sus propios tonos.
const SERIES_COLORS: Record<PaletteMode, string[]> = {
  light: [
    "#2a78d6",
    "#eb6834",
    "#1baf7a",
    "#eda100",
    "#e87ba4",
    "#008300",
    "#6250d6",
    "#e34948",
  ],
  dark: [
    "#3987e5",
    "#d95926",
    "#199e70",
    "#c98500",
    "#d55181",
    "#008300",
    "#9085e9",
    "#e66767",
  ],
};
export const MAX_SERIES = SERIES_COLORS.light.length;

export const seriesColor = (index: number, mode: PaletteMode): string =>
  SERIES_COLORS[mode][index % MAX_SERIES];

// Cada métrica tiene su color en toda la vista de cliente (el de su Sparkbar):
// impresiones en azul, clics en naranja e inversión (y sus costes) en verde.
// Con varias subcampañas, una gama de ese color que alterna tono y claridad
// para que se distingan; el primero es el de la Sparkbar.
const METRIC_COLORS: Record<
  "impressions" | "clicks" | "investment",
  Record<PaletteMode, string[]>
> = {
  impressions: {
    light: [
      "#1f5bff",
      "#0b3bb8",
      "#6b8fff",
      "#2a8bd6",
      "#4a3fd1",
      "#0a2a80",
      "#93b4ff",
      "#1b6fa8",
    ],
    dark: [
      "#4d7dff",
      "#9db5ff",
      "#2f5fe0",
      "#5ab0f0",
      "#8a80ff",
      "#c8d6ff",
      "#2848b8",
      "#7fd0ff",
    ],
  },
  clicks: {
    light: [
      "#ff6a13",
      "#c24400",
      "#ff9a52",
      "#e0a020",
      "#d9452b",
      "#8f3300",
      "#ffb27a",
      "#b87a00",
    ],
    dark: [
      "#ff7d33",
      "#ffb07a",
      "#e05a10",
      "#ffc24d",
      "#ff6f5a",
      "#ffd6b3",
      "#c84a10",
      "#e0a030",
    ],
  },
  investment: {
    light: [
      "#0fae68",
      "#06703f",
      "#5fcf98",
      "#0b8f7a",
      "#8fbf3a",
      "#044d2b",
      "#3bbf8c",
      "#7fa82e",
    ],
    dark: [
      "#33d68a",
      "#8de8b8",
      "#1d9c5f",
      "#3cc9b0",
      "#a8d65a",
      "#c8f3dc",
      "#12784a",
      "#66e0a8",
    ],
  },
};

// Los costes por unidad son dinero: van en verde, como la inversión.
const METRIC_FAMILY: Record<string, keyof typeof METRIC_COLORS> = {
  impressions: "impressions",
  clicks: "clicks",
  investment: "investment",
  cpm: "investment",
  cpc: "investment",
  cpv: "investment",
  cpa: "investment",
  cpe: "investment",
};

/**
 * Color de la serie N de una métrica: el suyo si lo tiene (impresiones,
 * clics, inversión) y, si no, el de la paleta general.
 */
export const metricSeriesColor = (
  metric: string | null | undefined,
  index: number,
  mode: PaletteMode
): string => {
  const family = metric ? METRIC_FAMILY[metric] : undefined;
  if (!family) return seriesColor(index, mode);
  const colors = METRIC_COLORS[family][mode];
  return colors[index % colors.length];
};

/** Todas las subcampañas, con la campaña a la que pertenecen. */
export const allSubcampaigns = (
  campaigns: ClientPortalCampaign[]
): { campaign: ClientPortalCampaign; sub: ClientPortalSubcampaign }[] =>
  campaigns.flatMap((campaign) =>
    campaign.subcampaigns.map((sub) => ({ campaign, sub }))
  );

// La vista de cliente tiene sus propias rutas para que el resto de roles también
// pueda abrirla desde el inicio; /campaigns/:id sigue siendo la página interna.
export const campaignPath = (campaignId: string) =>
  `/client/campaigns/${campaignId}`;
export const subcampaignPath = (campaignId: string, subcampaignId: string) =>
  `/client/campaigns/${campaignId}/subcampaigns/${subcampaignId}`;
